import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function FeaturesSections() {
  const sections = [
    {
      title: "Efficient, Affordable Estate Planning",
      description: "Modern estate planning that delivers premium results in half the time — without the traditional complexity or expensive attorney fees.",
      features: [
        'High-quality, Custom-Tailored Planning',
        'Attorney Reviewed Documents',
        'Legal Compliance Prioritized',
        'Rapid, Hassle-free Completion'
      ]
    },
    {
      title: "Attorney Support Every Step of the Way",
      description: "Get the guidance you need with direct access to licensed attorneys who review your documents and answer your questions.",
      features: [
        "Direct access to a licensed attorney for questions and guidance",
        "Real-time document review, commenting, and updates"
      ]
    },
    {
      title: "Coordinate Your Insurance & Estate Plan",
      description: "Ensure your life insurance, annuities, and beneficiary designations are aligned with your estate plan for complete protection.",
      features: [
        "Review and coordinate beneficiary designations",
        "Integrate insurance and annuity coverage",
        "Protect your family's financial future for generations"
      ]
    },
    {
      title: "Everything You Need in One Place",
      description: "From trusts and wills to powers of attorney and healthcare directives — your complete estate plan, all under one roof.",
      features: [
        "Trusts, wills, and powers of attorney",
        "Secure digital document storage and delivery",
        "Ongoing access to update your plan as life changes"
      ]
    }
  ];

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        style={{ scrollBehavior: 'smooth' }}
      >
        {sections.map((section, index) => (
          <section key={index} className="relative py-24 bg-white overflow-hidden">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative px-8 py-10 rounded-2xl backdrop-blur-sm"
              >
                <div className="absolute -inset-4 bg-gradient-to-r from-primary-50/30 via-secondary-50/40 to-primary-50/30 rounded-2xl -z-10" />
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-100/20 to-secondary-100/20 rounded-2xl -z-10" />

                <h2 className="text-3xl font-serif text-primary-700 sm:text-4xl">
                  <span className="relative inline-block">
                    {section.title}
                    <div className="absolute -bottom-2 left-0 w-full h-3 bg-secondary-100/40 -rotate-1 -z-[1]" />
                  </span>
                </h2>

                <p className="mt-4 text-lg text-secondary-600">
                  {section.description}
                </p>

                <div className="mt-8 space-y-6">
                  {section.features.map((feature, featureIndex) => (
                    <motion.div
                      key={feature}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: featureIndex * 0.2, duration: 0.5 }}
                      className="flex items-center p-3 rounded-lg hover:bg-primary-50/50 transition-colors duration-200"
                    >
                      <svg className="h-6 w-6 text-accent-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="ml-4 text-gray-700 font-medium">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8 flex justify-center">
                  <a
                    href="https://app.trustbuilder.law"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
                  >
                    Get Started
                  </a>
                </div>
              </motion.div>
            </div>
          </section>
        ))}
      </motion.div>
    </AnimatePresence>
  );
}

export default FeaturesSections; 